import React, { useCallback, useEffect, useContext } from 'react';
import type { RefObject } from 'react';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { LoadableLazy } from '@confluence/loadable';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { ADVANCED_SEARCH_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceSuccess,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';

import { useSearchResultsContext, QueryState } from '../../SearchResultsProvider.tsx';
import { useSearchInputContext } from '../../SearchInputProvider.tsx';
import { PageLoadEndEvent } from '../../PageLoadEndEvent.tsx';

import { SearchResultListNoInput } from './SearchResultListNoInput.tsx';
import { SearchResultListResults } from './SearchResultListResults.tsx';
import { SearchResultListLoading } from './SearchResultListLoading.tsx';
import { SearchResultListContainer } from './styled.tsx';

interface SearchResultListProps {
	resultsRef?: RefObject<HTMLInputElement>;
}

const SearchResultListError = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-SearchResultListError" */
				'./SearchResultListError.tsx'
			)
		).SearchResultListError,
});

export const SearchResultList = ({ resultsRef }: SearchResultListProps) => {
	const {
		queryState,
		loading,
		error,
		results,
		numResults,
		totalCount,
		hasNextPage,
		loadMore,
		abTest,
		experimentLayers,
		productEdition,
	} = useSearchResultsContext();
	const { showSearchAI } = useSearchInputContext();
	const prevResultsRef = React.useRef(results);
	const experienceTracker = useContext(ExperienceTrackerContext);

	useEffect(() => {
		prevResultsRef.current = results;
	}, [results]);

	const resultListRenderer = useCallback(() => {
		switch (queryState) {
			case QueryState.ERROR:
				// @ts-ignore error will always exist if QueryState.ERROR also exists, but ts cannot narrow it
				experienceTracker.fail({ error, name: ADVANCED_SEARCH_EXPERIENCE });
				return (
					<ErrorDisplay error={error as ApolloErrorInterop}>
						<SearchResultListContainer loading={false}>
							<SearchResultListError />
						</SearchResultListContainer>
					</ErrorDisplay>
				);
			// prevQueryStateRef to show the previous results while loading (if they exist),
			// otherwise show the no input state in a loading state
			case QueryState.LOADING:
				experienceTracker.start({ name: ADVANCED_SEARCH_EXPERIENCE });
				if (prevResultsRef.current.length) {
					return (
						<SearchResultListContainer loading>
							<SearchResultListResults
								results={prevResultsRef.current}
								numResults={numResults}
								totalCount={totalCount}
								hasNextPage={hasNextPage}
								loadMore={loadMore}
								queryState={queryState}
								abTest={abTest}
								isStale
							/>
						</SearchResultListContainer>
					);
				} else if (showSearchAI) {
					return <SearchResultListLoading />;
				} else {
					return (
						<SearchResultListContainer loading>
							<SearchResultListNoInput />
						</SearchResultListContainer>
					);
				}
			case QueryState.POSTQUERY:
				return (
					<SearchResultListContainer loading={false}>
						<SearchResultListResults
							results={results}
							numResults={numResults}
							totalCount={totalCount}
							hasNextPage={hasNextPage}
							loadMore={loadMore}
							resultListRef={resultsRef}
							queryState={queryState}
							abTest={abTest}
							experimentLayers={experimentLayers}
							isStale={false}
							productEdition={productEdition}
						/>
						<ExperienceSuccess name={ADVANCED_SEARCH_EXPERIENCE} />
					</SearchResultListContainer>
				);
			case QueryState.PREQUERY:
				return (
					// <SearchResultListLoading />
					<SearchResultListContainer loading={false}>
						<SearchResultListNoInput />
						<ExperienceSuccess name={ADVANCED_SEARCH_EXPERIENCE} />
					</SearchResultListContainer>
				);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [numResults, queryState, resultsRef, totalCount]);

	return (
		<>
			{resultListRenderer()}
			{!loading && <PageLoadEndEvent />}
		</>
	);
};
